/** Cluster detection — flood-fill BFS for color matching and floating detection. */

import { type HexCoord, coordToKey, hexNeighbors } from './hex';
import { type Bubble, type BoardState, type BubbleColor, ALL_BUBBLE_COLORS, getBubble } from '../types';
import { CONFIG } from '../config';

/**
 * Find all "support" gears — bubbles whose removal would cause at least one
 * *additional* bubble to become a floating (ceiling-disconnected) cluster
 * beyond what is already floating.
 *
 * Algorithm:
 *   1. Compute the baseline ceiling-connected set (flood from topmost row).
 *   2. For each non-obstacle candidate in that baseline set, re-flood with
 *      the candidate removed. If fewer bubbles are reached, the candidate
 *      was supporting at least one other ceiling-connected gear.
 *
 * O(N^2) over bubbles on the board. N is small (~50-100) so this is cheap.
 * Obstacles cannot be removed and are skipped as candidates but still act
 * as connectors during flood-fill (matching engine floating-detection).
 */
export function findSupportGears(board: BoardState): HexCoord[] {
  const supports: HexCoord[] = [];
  if (board.bubbles.size === 0) return supports;

  let topRow: number | null = null;
  for (const [, bubble] of board.bubbles) {
    if (topRow === null || bubble.coord.r < topRow) topRow = bubble.coord.r;
  }
  if (topRow === null) return supports;

  const baseline = floodFromTopRow(board, topRow, null);

  for (const candKey of baseline) {
    const cand = board.bubbles.get(candKey);
    if (!cand || cand.isObstacle) continue;

    const without = floodFromTopRow(board, topRow, candKey);
    // If removing `cand` shrinks the ceiling-connected set by more than itself,
    // at least one other gear was depending on it.
    if (without.size < baseline.size - 1) {
      supports.push(cand.coord);
    }
  }

  return supports;
}

function floodFromTopRow(
  board: BoardState,
  topRow: number,
  excludeKey: string | null,
): Set<string> {
  const visited = new Set<string>();
  const queue: HexCoord[] = [];

  for (const [key, bubble] of board.bubbles) {
    if (key === excludeKey) continue;
    if (bubble.coord.r === topRow) {
      visited.add(key);
      queue.push(bubble.coord);
    }
  }

  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const neighbor of hexNeighbors(current)) {
      const key = coordToKey(neighbor);
      if (visited.has(key)) continue;
      if (key === excludeKey) continue;
      const bubble = getBubble(board, neighbor);
      if (!bubble) continue;
      visited.add(key);
      queue.push(neighbor);
    }
  }

  return visited;
}

/**
 * Find all bubbles connected to `start` that share its color (flood-fill BFS).
 * Wild bubbles match any color and are included in the cluster.
 * Returns the coordinates of all matching bubbles (including `start`).
 *
 * When `start` itself is wild ("Rainbow Gear"), the card promises it "joins any
 * cluster it touches", so we flood against each distinct adjacent color and
 * return the largest resulting cluster. Without this, a wild seed falls back to
 * its hidden queue-assigned color and lands as a lone white gear next to a
 * matching-color crowd.
 *
 * The `options.minSize` parameter is reserved for future call sites that want
 * the helper to enforce a size threshold; today it is pass-through and the
 * threshold check stays at the caller (see engine.ts for the Close Match
 * override).
 */
export function findColorCluster(
  board: BoardState,
  start: HexCoord,
  _options?: { minSize?: number },
): HexCoord[] {
  const startBubble = getBubble(board, start);
  if (!startBubble) return [];

  if (startBubble.isWild) {
    const adjacent = new Set<BubbleColor>();
    for (const n of hexNeighbors(start)) {
      const nb = getBubble(board, n);
      if (nb && !nb.isObstacle) adjacent.add(nb.color);
    }

    let best: HexCoord[] = [start];
    for (const color of ALL_BUBBLE_COLORS) {
      if (!adjacent.has(color)) continue;
      const cluster = floodClusterByColor(board, start, color);
      if (cluster.length > best.length) best = cluster;
    }
    return best;
  }

  return floodClusterByColor(board, start, startBubble.color);
}

function floodClusterByColor(
  board: BoardState,
  start: HexCoord,
  targetColor: BubbleColor,
): HexCoord[] {
  const visited = new Set<string>();
  const cluster: HexCoord[] = [];
  const queue: HexCoord[] = [start];
  visited.add(coordToKey(start));

  while (queue.length > 0) {
    const current = queue.shift()!;
    const bubble = getBubble(board, current);
    if (!bubble) continue;

    if (bubble.isObstacle) continue;
    if (bubble.color !== targetColor && !bubble.isWild) continue;

    cluster.push(current);

    for (const neighbor of hexNeighbors(current)) {
      const key = coordToKey(neighbor);
      if (visited.has(key)) continue;
      visited.add(key);

      const neighborBubble = getBubble(board, neighbor);
      if (!neighborBubble) continue;
      if (!neighborBubble.isObstacle && (neighborBubble.color === targetColor || neighborBubble.isWild)) {
        queue.push(neighbor);
      }
    }
  }

  return cluster;
}

/**
 * Find every same-color cluster of size >= `minSize` (default MIN_CLUSTER_SIZE),
 * excluding the cluster that contains `excludeCoord`. Wilds are pulled in like
 * `findColorCluster`. Pass `minSize: 1` to return every connected same-color
 * group including singletons (used by Cascade Coil).
 */
export function findAllColorClusters(
  board: BoardState,
  color: BubbleColor,
  excludeCoord: HexCoord,
  minSize: number = CONFIG.MIN_CLUSTER_SIZE,
): HexCoord[][] {
  const excludeKey = coordToKey(excludeCoord);
  const visited = new Set<string>();

  const floodFrom = (seed: HexCoord): HexCoord[] => {
    const cluster: HexCoord[] = [];
    const queue: HexCoord[] = [seed];
    const seen = new Set<string>([coordToKey(seed)]);
    while (queue.length > 0) {
      const current = queue.shift()!;
      const bubble = getBubble(board, current);
      if (!bubble || bubble.isObstacle) continue;
      if (bubble.color !== color && !bubble.isWild) continue;
      cluster.push(current);
      for (const neighbor of hexNeighbors(current)) {
        const key = coordToKey(neighbor);
        if (seen.has(key)) continue;
        seen.add(key);
        const nb = getBubble(board, neighbor);
        if (!nb || nb.isObstacle) continue;
        if (nb.color === color || nb.isWild) {
          queue.push(neighbor);
        }
      }
    }
    return cluster;
  };

  const clusters: HexCoord[][] = [];
  for (const [key, bubble] of board.bubbles) {
    if (visited.has(key)) continue;
    if (bubble.isObstacle) continue;
    if (bubble.color !== color) continue;

    const cluster = floodFrom(bubble.coord);
    for (const c of cluster) visited.add(coordToKey(c));

    if (cluster.some(c => coordToKey(c) === excludeKey)) continue;
    if (cluster.length < minSize) continue;
    clusters.push(cluster);
  }
  return clusters;
}

/**
 * Find all floating clusters — bubbles NOT connected to the topmost occupied row.
 * Returns an array of coordinates for all floating bubbles.
 */
export function findFloatingClusters(board: BoardState): HexCoord[] {
  // Seed from the actual topmost occupied row rather than board.ceilingRow.
  // When a pop or bomb empties ceilingRow (e.g. a wild rainbow row is the ceiling),
  // seeding from a now-empty row would mark every remaining bubble as floating
  // and wipe the board.
  let topRow: number | null = null;
  for (const [, bubble] of board.bubbles) {
    if (topRow === null || bubble.coord.r < topRow) topRow = bubble.coord.r;
  }
  if (topRow === null) return [];

  const visited = new Set<string>();
  const queue: HexCoord[] = [];

  for (const [key, bubble] of board.bubbles) {
    if (bubble.coord.r === topRow) {
      visited.add(key);
      queue.push(bubble.coord);
    }
  }

  // Flood fill from ceiling
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const neighbor of hexNeighbors(current)) {
      const key = coordToKey(neighbor);
      if (visited.has(key)) continue;
      visited.add(key);

      const bubble = getBubble(board, neighbor);
      if (bubble) {
        queue.push(neighbor);
      }
    }
  }

  // Everything not visited is floating
  const floating: HexCoord[] = [];
  for (const [key, bubble] of board.bubbles) {
    if (!visited.has(key)) {
      floating.push(bubble.coord);
    }
  }

  return floating;
}
